import { Head, useForm, usePage } from "@inertiajs/react"

import DatePicker from "@/components/date-picker"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import AppLayout from "@/layouts/app-layout"
import type RequestsCreateOpportunity from "@/types/serializers/RequestsCreateOpportunity"

type ClubData = {
  id: number
  name: string
}

type Props = {
  scout_clubs?: ClubData[]
}

const POSITIONS = [
  { value: "portero", label: "Portero" },
  { value: "defensa_central", label: "Defensa Central" },
  { value: "lateral_derecho", label: "Lateral Derecho" },
  { value: "lateral_izquierdo", label: "Lateral Izquierdo" },
  { value: "mediocampista_central", label: "Mediocampista Central" },
  { value: "mediocampista_ofensivo", label: "Mediocampista Ofensivo" },
  { value: "mediocampista_defensivo", label: "Mediocampista Defensivo" },
  { value: "extremo_derecho", label: "Extremo Derecho" },
  { value: "extremo_izquierdo", label: "Extremo Izquierdo" },
  { value: "delantero_centro", label: "Delantero Centro" },
  { value: "segundo_delantero", label: "Segundo Delantero" },
]

export default function OpportunityNew({ scout_clubs }: Props) {
  const page = usePage()
  const auth = page.props.auth as { user: { role: string } }
  const isScout = auth?.user?.role === "scout"
  const showClubSelector = isScout && scout_clubs && scout_clubs.length >= 1
  const defaultClubId =
    isScout && scout_clubs && scout_clubs.length >= 1
      ? String(scout_clubs[0].id)
      : ""

  const { data, setData, post, processing, errors } =
    useForm<RequestsCreateOpportunity>({
      opportunity: {
        title: "",
        description: "",
        position_needed: "",
        country: "",
        region: "",
        division: "",
        min_age: null,
        max_age: null,
        expires_at: "",
        club_profile_id: defaultClubId || null,
      },
    })

  const setField = (
    field: keyof typeof data.opportunity,
    value: string | number | null,
  ) =>
    setData("opportunity", {
      ...data.opportunity,
      [field]: value,
    } as typeof data.opportunity)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post("/oportunidades")
  }

  return (
    <AppLayout>
      <Head title="Crear oportunidad | KrownFT" />

      <div className="min-h-svh">
        <div className="mx-auto max-w-2xl px-4 py-6">
          <h1 className="mb-6 text-2xl font-bold">Crear oportunidad</h1>

          <Card>
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-1.5">
                  <Label htmlFor="title">Título</Label>
                  <Input
                    id="title"
                    value={data.opportunity.title}
                    onChange={(e) => setField("title", e.target.value)}
                    placeholder="Ej: Buscamos delantero centro Sub-21"
                  />
                  {errors["opportunity.title"] && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors["opportunity.title"]}
                    </p>
                  )}
                </div>

                {showClubSelector && (
                  <div className="space-y-1.5">
                    <Label>Club</Label>
                    <Select
                      value={String(data.opportunity.club_profile_id || "")}
                      onValueChange={(v) => setField("club_profile_id", v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar club" />
                      </SelectTrigger>
                      <SelectContent>
                        {scout_clubs?.map((club) => (
                          <SelectItem key={club.id} value={String(club.id)}>
                            {club.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors["opportunity.club_profile_id"] && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors["opportunity.club_profile_id"]}
                      </p>
                    )}
                  </div>
                )}

                <div className="space-y-1.5">
                  <Label htmlFor="description">Descripción</Label>
                  <Textarea
                    id="description"
                    value={data.opportunity.description}
                    onChange={(e) => setField("description", e.target.value)}
                    placeholder="Describe los requisitos y detalles de la oportunidad..."
                    rows={5}
                  />
                  {errors["opportunity.description"] && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors["opportunity.description"]}
                    </p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label>Posición requerida</Label>
                  <Select
                    value={data.opportunity.position_needed}
                    onValueChange={(v) => setField("position_needed", v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar posición" />
                    </SelectTrigger>
                    <SelectContent>
                      {POSITIONS.map((p) => (
                        <SelectItem key={p.value} value={p.value}>
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors["opportunity.position_needed"] && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors["opportunity.position_needed"]}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="min_age">Edad mínima</Label>
                    <Input
                      id="min_age"
                      type="number"
                      value={data.opportunity.min_age ?? ""}
                      onChange={(e) => setField("min_age", e.target.value)}
                      placeholder="18"
                      min={14}
                      max={50}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="max_age">Edad máxima</Label>
                    <Input
                      id="max_age"
                      type="number"
                      value={data.opportunity.max_age ?? ""}
                      onChange={(e) => setField("max_age", e.target.value)}
                      placeholder="25"
                      min={14}
                      max={50}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="country">País</Label>
                    <Input
                      id="country"
                      value={data.opportunity.country}
                      onChange={(e) => setField("country", e.target.value)}
                      placeholder="Argentina"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="region">Región</Label>
                    <Input
                      id="region"
                      value={data.opportunity.region ?? ""}
                      onChange={(e) => setField("region", e.target.value)}
                      placeholder="Buenos Aires"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="division">División</Label>
                  <Input
                    id="division"
                    value={data.opportunity.division ?? ""}
                    onChange={(e) => setField("division", e.target.value)}
                    placeholder="Primera División"
                  />
                </div>

                <DatePicker
                  label="Fecha de vencimiento"
                  value={data.opportunity.expires_at ?? ""}
                  onChange={(v) => setField("expires_at", v)}
                  minDate={new Date().toISOString().slice(0, 10)}
                  maxDate={`${new Date().getFullYear() + 2}-12-31`}
                  placeholder="Seleccionar vencimiento..."
                />

                <Button type="submit" disabled={processing} className="w-full">
                  Publicar oportunidad
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  )
}
